/**
 * Minimal tooltip — appears on hover/focus after a short delay.
 * Follows DESIGN.md: off-black bg, white text, sharp 4px radius, no shadow.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function Tooltip({
  content,
  side = 'right',
  children,
  enabled = true,
}: {
  content: string;
  side?: 'right' | 'top' | 'bottom';
  children: ReactNode;
  enabled?: boolean;
}) {
  const [show, setShow] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const open = () => {
    if (!enabled) return;
    timer.current = setTimeout(() => setShow(true), 200);
  };
  const close = () => {
    clearTimeout(timer.current);
    setShow(false);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className="relative" onMouseEnter={open} onMouseLeave={close} onFocus={open} onBlur={close}>
      {children}
      {show && (
        <span
          role="tooltip"
          className={cn(
            'absolute z-50 whitespace-nowrap rounded-sm px-2.5 py-1.5 text-[12px] font-medium',
            'bg-foreground text-surface pointer-events-none select-none',
            'animate-in fade-in-0 zoom-in-95 duration-150',
            side === 'right' && 'left-full top-1/2 -translate-y-1/2 ml-2',
            side === 'top' && 'bottom-full left-1/2 -translate-x-1/2 mb-2',
            side === 'bottom' && 'top-full left-1/2 -translate-x-1/2 mt-2',
          )}
        >
          {content}
        </span>
      )}
    </div>
  );
}
